import React, { useState, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Button } from 'antd';
import { toast } from '../Toast';
import { signInWithCustomToken, getIdToken } from '../../helpers/auth';
import { loginUser } from '../../ducks/Auth/actions';
import {
  fetchClientInviteContext,
  acceptClientInvite,
} from '../../services/clientApi';
import { ASSESSMENT_TYPES } from '../../config/assessmentConfigs';
import NubodLogo from '../../assets/logos/nubod-black.png';
import './index.css';

// Mirrors the server-side fallback in GET /users/client-invite/:token
// — used when the inviting trainer has no business branding set, so
// the page degrades to a generic Nubod look without breaking the
// brand-driven inline styles below.
const DEFAULT_BRAND = {
  name: 'Nubod',
  colors: { primary: '#8b5cf6', secondary: '#06b6d4', accent: '#10b981' },
  logo: null,
  source: 'default',
};

/**
 * /client-setup/:token landing page.
 *
 * Trainer-branded welcome screen the client lands on from the
 * welcome-email link. Intentionally NO password form — the canonical
 * "pick your password" step is the last question of the assessment
 * (`restPassword`, see config/assessmentConfigs.js). This page just:
 *
 *   1. Validates the (token, email) pair on mount via
 *      GET /users/client-invite/:token (so the trainer brand + the
 *      client's first name can render before they click anything,
 *      and bad links surface a clear error instead of bouncing on
 *      submit).
 *   2. Shows a branded "Welcome, …" card with a single "Get started"
 *      CTA. Requiring a click before consuming the token also
 *      protects against email-security-gateway URL prefetchers
 *      auto-redeeming the invite before the human sees the page.
 *   3. On click, POSTs to /users/accept-client-invite, signs the
 *      client in with the returned Firebase custom token, and
 *      forwards them into the assessment flow — same routing the
 *      regular login page uses, so they end up in exactly the same
 *      place they would have under the old "emailed password" flow.
 */
const ClientSetup = ({ match }) => {
  // Token from the URL path; email from `?email=` query — both come
  // from the welcome-email link the trainer's invite generated. The
  // server requires (token, email) to match the persisted invite, so
  // wiring both is mandatory.
  const token = match?.params?.token;
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const emailFromUrl =
    new URLSearchParams(location.search).get('email') || '';

  const [contextLoading, setContextLoading] = useState(true);
  const [contextError, setContextError] = useState(null);
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [firstName, setFirstName] = useState('');
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch trainer-brand context on mount so we can render in the
  // trainer's colors before the client clicks "Get started". Bails
  // out with a friendly error if the link is malformed, expired, or
  // already used — the server returns a generic 404 either way to
  // avoid leaking whether the token or email is the bad piece.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (!token || !emailFromUrl) {
        if (!cancelled) {
          setContextError(
            'This invite link is missing information. Please ask your trainer to resend it.',
          );
          setContextLoading(false);
        }
        return;
      }
      try {
        const ctx = await fetchClientInviteContext(token, emailFromUrl);
        if (cancelled) return;
        setBrand(ctx.brand || DEFAULT_BRAND);
        setFirstName(ctx.client?.firstName || '');
      } catch (err) {
        if (cancelled) return;
        const msg =
          err?.body?.errors?.[0]?.msg ||
          (err?.status === 410
            ? 'This invite link has expired. Ask your trainer to resend it.'
            : 'This invite link is invalid or has already been used.');
        setContextError(msg);
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [token, emailFromUrl]);

  const handleGetStarted = async () => {
    setSubmitLoading(true);
    try {
      const { customToken, user: acceptedUser } = await acceptClientInvite({
        token,
        email: emailFromUrl,
      });
      if (customToken) {
        // Sign in via the custom token so the client lands in the
        // assessment already authenticated.
        await signInWithCustomToken(customToken);
        const idToken = await getIdToken();
        localStorage.setItem('token', idToken);
        try {
          localStorage.setItem('viewMode', 'client');
        } catch (_) {
          /* localStorage write can fail in private-mode browsers */
        }

        // Same post-login routing rule pages/dashboard/login.js
        // applies: a fresh client always has `isAssessmentCompleted:
        // false`, so this collapses to `/assessment` (or
        // `/quick-setup` for the rare `assessmentType: 'none'`
        // path). The assessment itself ends with the `restPassword`
        // step, which is where the client picks the real password
        // they'll sign in with from now on.
        let target = '/dashboard/home';
        if (!acceptedUser?.isAssessmentCompleted) {
          target =
            acceptedUser?.assessmentType === ASSESSMENT_TYPES.NONE
              ? '/quick-setup'
              : '/assessment';
        }

        // Redux's `auth.user` / `auth.isAuthenticated` only get
        // populated by the `loginUser` saga (which POSTs /auth/login
        // and then runs `loadUser`). Without it, pushing straight to
        // /assessment renders the page before Redux is hydrated,
        // upstream guards see "not authenticated" and bounce to
        // /login — exactly the symptom we saw. Mirroring
        // AuthModal's flow: dispatch `loginUser` with the fresh id
        // token and push inside the callback that fires *after*
        // loadUser completes.
        dispatch(
          loginUser({ token: idToken }, (error) => {
            if (error) {
              toast.error(
                'Could not sign you in. Please use the email link again or contact your trainer.',
              );
              setSubmitLoading(false);
              return;
            }
            history.push(target);
          }),
        );
      } else {
        // Server consumed the invite but couldn't mint a custom
        // token (rare — firebase-admin credential issue). The
        // account exists but the client has no password yet, so
        // /login alone won't work. Surface the failure rather than
        // silently dropping them somewhere broken.
        toast.error(
          'Could not sign you in automatically. Please ask your trainer to resend the invite.',
        );
        setSubmitLoading(false);
      }
    } catch (err) {
      const msg =
        err?.body?.errors?.[0]?.msg ||
        'Could not activate your account. Please try again.';
      toast.error(msg);
      setSubmitLoading(false);
    }
  };

  const primary = brand?.colors?.primary || DEFAULT_BRAND.colors.primary;
  const businessName = brand?.name || DEFAULT_BRAND.name;
  // Only treat as branded when the server gave us a real trainer /
  // organization brand. Otherwise the page renders in the generic
  // Nubod chrome (purple primary, Nubod wordmark).
  const isTrainerBranded = !!(brand?.source && brand.source !== 'default');

  if (contextLoading) {
    return (
      <div className='client-setup-page'>
        <div className='client-setup-state'>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  if (contextError) {
    return (
      <div className='client-setup-page'>
        <div className='client-setup-state'>
          <h2>Invite link issue</h2>
          <p>{contextError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className='client-setup-page'>
      <header
        className='client-setup-hero'
        style={{ background: primary }}
      >
        {brand?.logo ? (
          <img
            src={brand.logo}
            alt={businessName}
            className='client-setup-logo'
          />
        ) : (
          <img
            src={NubodLogo}
            alt='Nubod'
            className='client-setup-logo client-setup-logo--invert'
          />
        )}
      </header>

      <div className='client-setup-card'>
        <h2 className='client-setup-title'>
          {firstName ? `Welcome, ${firstName}!` : 'Welcome!'}
        </h2>
        <p className='client-setup-sub'>
          {isTrainerBranded
            ? `${businessName} set up your account. Finishing takes a couple of minutes, and you'll set your password at the end.`
            : 'Your account is ready. Finishing takes a couple of minutes, and you\'ll set your password at the end.'}
        </p>

        <Button
          type='primary'
          onClick={handleGetStarted}
          loading={submitLoading}
          className='client-setup-submit'
          style={{ background: primary, borderColor: primary }}
        >
          Get started
        </Button>

        {isTrainerBranded ? (
          <p className='client-setup-footer'>Powered by Nubod</p>
        ) : null}
      </div>
    </div>
  );
};

export default ClientSetup;
